/**
 * 支付状态管理
 * 使用 @vue/reactivity 实现响应式状态
 */

import { ref, computed, type Ref, type ComputedRef } from '@vue/reactivity'
import type { IStorage } from '../storage/Storage'
import type { Package, Order, PaymentQRCode, Coupon, PaymentState } from '../../types'
import { STORAGE_KEYS } from '../../constants/storage-keys'
import { BaseStore } from './BaseStore'

export class PaymentStore extends BaseStore<PaymentState> {
  // 响应式状态
  private _packages: Ref<Package[]>
  private _currentOrder: Ref<Order | null>
  private _paymentQRCode: Ref<PaymentQRCode | null>
  private _coupons: Ref<Coupon[]>

  // 计算属性
  public readonly hasOrder: ComputedRef<boolean>
  public readonly hasPaymentQRCode: ComputedRef<boolean>
  public readonly availableCoupons: ComputedRef<Coupon[]>

  constructor(storage: IStorage) {
    super(storage, STORAGE_KEYS.PACKAGES)

    // 初始化响应式状态
    this._packages = ref<Package[]>([])
    this._currentOrder = ref<Order | null>(null)
    this._paymentQRCode = ref<PaymentQRCode | null>(null)
    this._coupons = ref<Coupon[]>([])

    // 初始化计算属性
    this.hasOrder = computed(() => {
      return this._currentOrder.value !== null
    })

    this.hasPaymentQRCode = computed(() => {
      return this._paymentQRCode.value !== null
    })

    this.availableCoupons = computed(() => {
      return this._coupons.value.filter((coupon) => !coupon.is_used)
    })

    // 从存储中恢复状态
    this.hydrate()
  }

  /**
   * 获取套餐列表
   */
  get packages(): Package[] {
    return this._packages.value
  }

  /**
   * 设置套餐列表
   */
  set packages(value: Package[]) {
    this._packages.value = value
    this.persist()
  }

  /**
   * 获取当前订单
   */
  get currentOrder(): Order | null {
    return this._currentOrder.value
  }

  /**
   * 设置当前订单
   */
  set currentOrder(value: Order | null) {
    this._currentOrder.value = value
    this.persist()
  }

  /**
   * 获取支付二维码
   */
  get paymentQRCode(): PaymentQRCode | null {
    return this._paymentQRCode.value
  }

  /**
   * 设置支付二维码
   */
  set paymentQRCode(value: PaymentQRCode | null) {
    this._paymentQRCode.value = value
    // 支付二维码不需要持久化
  }

  /**
   * 获取优惠券列表
   */
  get coupons(): Coupon[] {
    return this._coupons.value
  }

  /**
   * 设置优惠券列表
   */
  set coupons(value: Coupon[]) {
    this._coupons.value = value
    this.persist()
  }

  /**
   * 添加优惠券
   */
  addCoupon(coupon: Coupon): void {
    this._coupons.value.push(coupon)
    this.persist()
  }

  /**
   * 使用优惠券
   */
  useCoupon(couponCode: string): void {
    const coupon = this._coupons.value.find((c) => c.code === couponCode)
    if (coupon) {
      coupon.is_used = true
      this.persist()
    }
  }

  /**
   * 根据 ID 获取套餐
   */
  getPackageById(id: number): Package | undefined {
    return this._packages.value.find((pkg) => pkg.id === id)
  }

  /**
   * 根据类型获取套餐列表
   * 注意：Package 没有 type 字段，此方法保留用于兼容，但始终返回空数组
   * 如需按类型筛选，请使用 soft_edition 或 validity_type 字段
   */
  getPackagesByType(_type: string): Package[] {
    // Package 类型没有 type 字段，返回空数组
    // 调用方应使用其他方式筛选套餐
    return []
  }

  /**
   * 清除支付信息
   */
  clearPaymentInfo(): void {
    this._currentOrder.value = null
    this._paymentQRCode.value = null
    this.persist()
  }

  /**
   * 清除所有数据
   */
  clearAll(): void {
    this._packages.value = []
    this._currentOrder.value = null
    this._paymentQRCode.value = null
    this._coupons.value = []
    this.clear()
  }

  /**
   * 序列化状态
   */
  serialize(): string {
    const state = {
      packages: this._packages.value,
      currentOrder: this._currentOrder.value,
      coupons: this._coupons.value
    }
    return JSON.stringify(state)
  }

  /**
   * 反序列化状态
   */
  deserialize(data: string): void {
    try {
      const state = JSON.parse(data)
      this._packages.value = state.packages || []
      this._currentOrder.value = state.currentOrder || null
      this._coupons.value = state.coupons || []
    } catch (error) {
      console.error('[PaymentStore] Failed to deserialize state', error)
    }
  }

  /**
   * 获取当前状态
   */
  getState(): PaymentState {
    return {
      packages: this._packages.value,
      currentOrder: this._currentOrder.value,
      paymentQRCode: this._paymentQRCode.value,
      coupons: this._coupons.value
    }
  }
}
